import PageToolbar from '@/components/linear/page-toolbar';
import {
  profilePath,
  settingsPasswordPath,
  settingsSessionPath,
  settingsSessionsOthersPath,
} from '@/lib/linear-paths';
import { formatRelativeTime } from '@/lib/time';
import { Form, router } from '@inertiajs/react';

interface ProfileUser {
  id: string;
  name: string;
  emailAddress: string;
}

interface SessionRow {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  current: boolean;
}

interface Props {
  user: ProfileUser;
  sessions?: SessionRow[];
  error?: string | null;
}

export default function Profile({ user, sessions = [], error }: Props) {
  const otherSessions = sessions.filter((session) => !session.current);

  return (
    <div className="ln-page">
      <PageToolbar title="Profile and security" showNew={false} />
      <div className="ln-page-scroll" style={{ padding: 24, flex: 1, maxWidth: 720 }}>
        {error ? (
          <div className="ln-auth-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        ) : null}

        <h2 style={{ fontSize: '0.9375rem', fontWeight: 590, marginBottom: 16 }}>Profile</h2>
        <Form method="patch" action={profilePath()} style={{ marginBottom: 32 }}>
          {({ processing, errors }) => (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)', marginBottom: 6 }}>
                  Name
                </div>
                <input
                  className="ln-filter-input"
                  name="user[name]"
                  defaultValue={user.name}
                  autoComplete="name"
                  required
                  aria-label="Name"
                  style={{ width: '100%' }}
                />
                {errors.name ? <div className="ln-auth-error">{errors.name}</div> : null}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)', marginBottom: 6 }}>
                  Email
                </div>
                <input
                  className="ln-filter-input"
                  type="email"
                  name="user[emailAddress]"
                  defaultValue={user.emailAddress}
                  autoComplete="email"
                  required
                  aria-label="Email"
                  style={{ width: '100%' }}
                />
                {errors.emailAddress ? (
                  <div className="ln-auth-error">{errors.emailAddress}</div>
                ) : null}
              </div>
              <button type="submit" className="ln-primary-btn" disabled={processing}>
                Save profile
              </button>
            </>
          )}
        </Form>

        <h2 style={{ fontSize: '0.9375rem', fontWeight: 590, marginBottom: 16 }}>Password</h2>
        <Form method="patch" action={settingsPasswordPath()} style={{ marginBottom: 32 }}>
          {({ processing, errors }) => (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)', marginBottom: 6 }}>
                  Current password
                </div>
                <input
                  className="ln-filter-input"
                  type="password"
                  name="currentPassword"
                  autoComplete="current-password"
                  required
                  aria-label="Current password"
                  style={{ width: '100%' }}
                />
                {errors.currentPassword ? (
                  <div className="ln-auth-error">{errors.currentPassword}</div>
                ) : null}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)', marginBottom: 6 }}>
                  New password
                </div>
                <input
                  className="ln-filter-input"
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  aria-label="New password"
                  placeholder="12+ characters"
                  style={{ width: '100%' }}
                />
                {errors.password ? <div className="ln-auth-error">{errors.password}</div> : null}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)', marginBottom: 6 }}>
                  Confirm new password
                </div>
                <input
                  className="ln-filter-input"
                  type="password"
                  name="passwordConfirmation"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  aria-label="Confirm new password"
                  style={{ width: '100%' }}
                />
                {errors.passwordConfirmation ? (
                  <div className="ln-auth-error">{errors.passwordConfirmation}</div>
                ) : null}
              </div>
              <button type="submit" className="ln-primary-btn" disabled={processing}>
                Update password
              </button>
            </>
          )}
        </Form>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            gap: 12,
          }}
        >
          <h2 style={{ fontSize: '0.9375rem', fontWeight: 590, margin: 0 }}>Sessions</h2>
          {otherSessions.length > 0 ? (
            <button
              type="button"
              className="ln-toolbar-btn"
              onClick={() => {
                if (window.confirm('Sign out of all other devices?')) {
                  router.delete(settingsSessionsOthersPath());
                }
              }}
            >
              Sign out other sessions
            </button>
          ) : null}
        </div>
        {sessions.map((session) => (
          <div key={session.id} className="ln-settings-row">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 510 }}>{session.userAgent?.trim() || 'Unknown device'}</div>
              <div className="ln-inbox-sub">
                {session.ipAddress || 'Unknown IP'}
                {' · '}
                {formatRelativeTime(session.createdAt)}
              </div>
            </div>
            {session.current ? (
              <span className="ln-label">Current</span>
            ) : (
              <button
                type="button"
                className="ln-toolbar-btn"
                onClick={() => {
                  if (window.confirm('Revoke this session?')) {
                    router.delete(settingsSessionPath(session.id));
                  }
                }}
              >
                Revoke
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
